if (!customElements.get('ncm-auto-fbt')) {
  const ICONS = {
    shirt: `<svg viewBox="0 0 488 488" fill="none" aria-hidden="true"><path d="M182 106C194 116 215 121 244 121C273 121 294 116 306 106C321 94 331 63 336 41L348 45C391 60 430 81 463 107C468 111 470 114 471 116C470 119 469 122 467 126L434 196C432 201 430 204 429 205C427 205 424 205 420 204L351 191V425C351 428 350 429 350 429C349 430 347 430 344 430H144C141 430 139 430 138 429C137 428 137 426 137 423V191L68 204C64 205 61 205 59 205C58 204 56 201 54 197L20 126C18 122 17 119 16 116C17 114 20 111 25 107C59 81 98 60 141 46L152 42C157 63 167 94 182 106Z" stroke="currentColor" stroke-width="20" stroke-linejoin="round" stroke-linecap="round"/></svg>`,
    check: `<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/><path d="M8 12.5L10.8 15L16 9.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
    truck: (width) =>
      `<svg viewBox="0 0 24 24" fill="none"><path d="M3 7.5h11v9H3v-9Z" stroke="currentColor" stroke-width="${width}"/><path d="M14 10h3.2l2.8 3.2v3.3h-6V10Z" stroke="currentColor" stroke-width="${width}"/><circle cx="7" cy="18" r="1.7" stroke="currentColor" stroke-width="${width}"/><circle cx="17" cy="18" r="1.7" stroke="currentColor" stroke-width="${width}"/></svg>`,
  };

  const RECOMMENDATION_POOL = 15;

  const escapeHtml = (value) =>
    String(value ?? '').replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
    );

  const PLACEHOLDER = /\{(\w+)\}/g;
  const fillTemplate = (template, values) =>
    String(template || '').replace(PLACEHOLDER, (match, key) => (key in values ? values[key] : match));

  // products.json (collection fallback) returns prices as decimal strings; the other sources use cents.
  const toCents = (value) => (typeof value === 'string' ? Math.round(parseFloat(value) * 100) || 0 : Number(value) || 0);

  const imageSrc = (image) => (typeof image === 'string' ? image : image?.src || '');

  const sizedImage = (src, width) => {
    if (!src) return '';
    try {
      const url = new URL(src.startsWith('//') ? `https:${src}` : src, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch {
      return src;
    }
  };

  const shuffle = (list) => [...list].sort(() => Math.random() - 0.5);

  const optionRank = (name) => (/size/i.test(name) ? 0 : /colou?r/i.test(name) ? 1 : 2);

  const variantOptions = (variant) =>
    variant.options || [variant.option1, variant.option2, variant.option3].filter((value) => value != null);

  const normalizeProduct = (product) => {
    const rawOptions = product.options || [];
    const optionNames = rawOptions.map((option) => (typeof option === 'string' ? option : option.name));
    const productImage = imageSrc(product.featured_image) || imageSrc(product.images?.[0]);
    const variants = (product.variants || []).map((variant) => ({
      id: variant.id,
      title: variant.title,
      options: variantOptions(variant),
      price: toCents(variant.price),
      compare: toCents(variant.compare_at_price),
      image: imageSrc(variant.featured_image) || productImage,
      available: Boolean(variant.available),
    }));
    const optionValues = optionNames.map((name, index) => {
      const raw = rawOptions[index];
      if (raw && typeof raw === 'object' && Array.isArray(raw.values) && raw.values.length) return [...raw.values];
      return variants.reduce((values, variant) => {
        const value = variant.options[index];
        if (value != null && !values.includes(value)) values.push(value);
        return values;
      }, []);
    });

    return {
      title: product.title,
      handle: product.handle,
      type: product.type ?? product.product_type ?? '',
      optionNames,
      optionValues,
      image: productImage,
      variants,
    };
  };

  /*
    Formats cents with the storefront's own money format. The section renders a sample amount with
    Shopify's money filters (presentment currency), and this reuses its symbol, separators and decimals.
  */
  const createMoneyFormatter = ({ currency, sample, sampleWithCurrency, codeEnabled }) => {
    const parse = (text) => {
      const match = String(text || '').match(/\d[\d.,'\s\u00a0\u202f]*\d/);
      if (!match) return null;
      const number = match[0];
      const decimal = number.match(/([.,])(\d{2})$/);
      const integerPart = decimal ? number.slice(0, -3) : number;
      return {
        prefix: text.slice(0, match.index),
        suffix: text.slice(match.index + number.length),
        decimals: decimal ? 2 : 0,
        decimalSeparator: decimal ? decimal[1] : '',
        groupSeparator: integerPart.match(/\d(\D+)\d{3}$/)?.[1] ?? '',
      };
    };

    const base = parse(sample);
    const withCurrency = parse(sampleWithCurrency) || base;
    let fallback;
    try {
      fallback = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency });
    } catch {
      fallback = null;
    }

    return (cents, withCode = false) => {
      const value = Math.abs(Math.round(Number(cents) || 0));
      const pattern = withCode && codeEnabled ? withCurrency : base;
      if (!pattern) return fallback ? fallback.format(value / 100) : (value / 100).toFixed(2);

      const integer = pattern.decimals ? Math.floor(value / 100) : Math.round(value / 100);
      const grouped = String(integer).replace(/\B(?=(\d{3})+(?!\d))/g, pattern.groupSeparator);
      const fraction = pattern.decimals ? `${pattern.decimalSeparator}${String(value % 100).padStart(2, '0')}` : '';
      return `${pattern.prefix}${grouped}${fraction}${pattern.suffix}`;
    };
  };

  class NcmAutoFbt extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;

      try {
        this.config = JSON.parse(this.querySelector('[data-ncm-fbt-config]')?.textContent || '{}');
      } catch {
        this.config = {};
      }
      this.bundle = window.NcmBundle;
      this.config.discount = { method: this.bundle?.config.method || 'none', tiers: this.bundle?.config.tiers || [] };
      this.config.messages = this.bundle?.config.messages || {};
      this.config.freeShipping = this.bundle?.config.freeShipping || {};
      this.formatMoneyValue = createMoneyFormatter(this.config.money || {});

      this.items = [];
      this.cart = { eligibleQuantity: 0, total: 0 };
      this.lastTierKey = null;
      this.wasShippingUnlocked = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const $ = (selector) => this.querySelector(selector);
      this.els = {
        items: $('[data-ncm-auto-fbt-items]'),
        button: $('[data-ncm-fbt-add]'),
        message: $('[data-ncm-fbt-message]'),
        note: $('[data-ncm-fbt-note]'),
        total: $('[data-ncm-fbt-total]'),
        originalTotal: $('[data-ncm-fbt-original-total]'),
        wasPrice: $('[data-ncm-fbt-was-price]'),
        saveBadge: $('[data-ncm-fbt-save]'),
        saveText: $('[data-ncm-fbt-save-text]'),
        breakdown: $('[data-ncm-fbt-breakdown]'),
        saleRow: $('[data-ncm-fbt-sale-row]'),
        saleSavings: $('[data-ncm-fbt-sale-savings]'),
        bundleRow: $('[data-ncm-fbt-bundle-row]'),
        bundleLabel: $('[data-ncm-fbt-bundle-label]'),
        bundleSavings: $('[data-ncm-fbt-bundle-savings]'),
        statusRow: $('[data-ncm-fbt-status-row]'),
        bundleStatus: $('[data-ncm-fbt-bundle-status]'),
        bundleIcon: $('[data-ncm-fbt-bundle-icon]'),
        bundleText: $('[data-ncm-fbt-bundle-text]'),
        shippingNote: $('[data-ncm-fbt-shipping-note]'),
        shippingIcon: $('[data-ncm-fbt-shipping-icon]'),
        shippingText: $('[data-ncm-fbt-shipping-text]'),
        modal: $('[data-ncm-fbt-modal]'),
        modalImg: $('[data-ncm-fbt-modal-img]'),
        modalClose: $('[data-ncm-fbt-modal-close]'),
      };

      try {
        this.currentProduct = JSON.parse(this.dataset.currentProduct || '{}');
      } catch {
        this.currentProduct = {};
      }

      this.els.button?.addEventListener('click', () => this.addToCart(), { signal });
      this.els.items?.addEventListener('change', (event) => this.onItemChange(event), { signal });
      this.els.items?.addEventListener('click', (event) => this.onItemsClick(event), { signal });
      this.els.items?.addEventListener('keydown', (event) => this.onDropdownKeydown(event), { signal });
      this.els.modalClose?.addEventListener('click', () => this.closeModal(), { signal });
      this.els.modal?.addEventListener('click', (event) => event.target === this.els.modal && this.closeModal(), {
        signal,
      });
      document.addEventListener('pointerdown', (event) => this.onDocumentPointer(event), { signal });
      document.addEventListener('keydown', (event) => this.onDocumentKeydown(event), { signal });
      window.addEventListener('resize', () => this.positionOpenDropdown(), { signal });
      window.addEventListener('scroll', () => this.positionOpenDropdown(), { passive: true, signal });
      document.addEventListener('change', (event) => this.onMainOptionChange(event), { signal });
      document.addEventListener(
        'ncm:selection-change',
        (event) => {
          if (event.target !== this.mainProduct || !this.items?.length) return;
          this.syncCurrentItemFromMain();
          const size = this.getMainSize();
          if (size) {
            this.items
              .filter((item) => !item.isCurrent)
              .forEach((item) => {
                const index = this.optionIndexByName(item.product, /size/i);
                if (index < 0 || !item.product.optionValues[index]?.includes(size)) return;
                item.selected[index] = size;
                this.applySelection(item);
              });
          }
          this.items.forEach((item) => this.renderItem(item));
          this.updateTotal();
        },
        { signal }
      );
      // The main product section can be re-rendered on its own (theme editor), which replaces the teaser.
      document.addEventListener(
        'product-info:loaded',
        (event) => event.target === this.mainProduct && this.items.length && this.showTeaser(),
        { signal }
      );
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.loadCart());
      }

      this.load();
      this.loadCart();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.teaserController?.abort();
      this.unsubscribeCart?.();
      this.toggleScrollLock(false);
      this.closeDropdowns();
      if (this.teaser?.dataset.ncmFbtSource === this.dataset.sectionId) this.teaser.hidden = true;
    }

    get mainProduct() {
      return document.querySelector(
        `product-info[id^="MainProduct-"]:not([data-original-section])[data-product-id="${this.config.productId}"]`
      );
    }

    get cartUrl() {
      return window.routes?.cart_url || '/cart';
    }

    formatMoney(cents, withCode = false) {
      return this.formatMoneyValue(cents, withCode);
    }

    itemWord(count) {
      const { itemSingular, itemPlural } = this.config.messages;
      return count === 1 ? itemSingular || 'shirt' : itemPlural || 'shirts';
    }

    isEligibleType(type) {
      return this.bundle ? this.bundle.isEligibleType(type) : true;
    }

    // Bundle rules (active tiers, best tier, next tier) live in ncm-bundle.js, shared with the cart drawer.
    get activeTiers() {
      return this.bundle ? this.bundle.activeTiers() : [];
    }

    tierFor(quantity) {
      return this.bundle ? this.bundle.tierFor(quantity) : null;
    }

    // FBT nudges toward its goal tier (three shirts by default), never past it.
    nextTier(quantity, current) {
      if (!this.bundle) return null;
      const goal = this.bundle.goalTier?.();
      if (!goal) return this.bundle.nextTier(quantity, current);
      return quantity < goal.quantity && goal.percent > (current?.percent || 0) ? goal : null;
    }

    async fetchCart() {
      const response = await fetch(`${this.cartUrl}.js`, { headers: { Accept: 'application/json' } });
      return response.json();
    }

    // Shirts already in the cart count toward Shopify's minimum quantity, and the cart total toward free shipping.
    async loadCart() {
      try {
        const cart = await this.fetchCart();
        if (this.controller.signal.aborted) return;
        const eligibleQuantity = this.bundle ? this.bundle.eligibleQuantity(cart) : 0;
        this.cart = { eligibleQuantity, total: Number(cart.total_price) || 0 };
      } catch {
        this.cart = { eligibleQuantity: 0, total: 0 };
      }
      if (this.items.length) this.updateTotal();
    }

    async load() {
      const { signal } = this.controller;
      let products = [];
      try {
        products = await this.fetchRecommendations(signal);
        if (!products.length) products = await this.fetchCollectionFallback(signal);
      } catch {
        if (signal.aborted) return;
        try {
          products = await this.fetchCollectionFallback(signal);
        } catch {
          products = [];
        }
      }
      if (signal.aborted) return;
      this.render(products);
    }

    async fetchRecommendations(signal) {
      const url = `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${this.config.productId}&limit=${RECOMMENDATION_POOL}&intent=related`;
      const response = await fetch(url, { signal });
      const data = await response.json();
      return shuffle((data.products || []).filter((product) => !this.isAddOn(product))).slice(0, this.config.limit);
    }

    async fetchCollectionFallback(signal) {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}collections/all/products.json?limit=20`, {
        signal,
      });
      const data = await response.json();
      return shuffle(
        (data.products || []).filter((product) => product.handle !== this.config.handle && !this.isAddOn(product))
      ).slice(
        0,
        this.config.limit
      );
    }

    // Package protection and other non-shipping add-ons are never bundle picks.
    isAddOn(product) {
      return Boolean(this.bundle?.isAddOnProduct?.(product));
    }

    render(products) {
      const recommended = products.filter((product) => product.handle !== this.config.handle);
      this.items = [this.currentProduct, ...recommended]
        .map(normalizeProduct)
        .filter((product) => product.variants.length)
        .slice(0, this.config.limit + 1)
        .map((product, index) => {
          const isCurrent = index === 0 && product.handle === this.config.handle;
          const selected = this.initialSelected(product, isCurrent);
          return {
            product,
            selected,
            variant: this.exactVariant(product, selected) || this.representativeVariant(product, selected),
            quantity: 1,
            isCurrent,
          };
        });

      if (!this.items.length) {
        this.closest('section')?.setAttribute('hidden', '');
        return;
      }

      this.els.items.innerHTML = this.items.map((item, index) => this.itemMarkup(item, index)).join('');
      this.items.forEach((item, index) => {
        item.el = this.els.items.children[index];
      });

      this.syncCurrentItemFromMain();
      this.items.forEach((item) => this.renderItem(item));
      this.updateTotal();

      if (this.els.button) this.els.button.hidden = false;
      this.showTeaser();
    }

    itemMarkup({ product, isCurrent }, index) {
      const title = isCurrent ? `This item: ${product.title}` : product.title;
      const hasOptions = !(product.variants.length === 1 && product.variants[0].title === 'Default Title');
      const selects = hasOptions
        ? product.optionNames
            .map((name, optionIndex) => ({ name, optionIndex }))
            .sort((a, b) => optionRank(a.name) - optionRank(b.name) || a.optionIndex - b.optionIndex)
            .map(({ name, optionIndex }) => this.dropdownMarkup(product, name, optionIndex, index))
            .join('')
        : '';

      return `
        <div class="ncm-fbt__item" data-current-item="${isCurrent}" data-index="${index}">
          <label class="ncm-fbt__check">
            <input type="checkbox" checked data-ncm-fbt-check aria-label="${escapeHtml(`Include ${product.title}`)}">
            <span aria-hidden="true"></span>
          </label>
          <button type="button" class="ncm-fbt__image" data-ncm-fbt-image-button aria-label="${escapeHtml(`Enlarge image of ${product.title}`)}">
            ${product.image ? `<img src="${escapeHtml(sizedImage(product.image, 240))}" alt="${escapeHtml(product.title)}" width="120" height="120" loading="lazy" data-ncm-fbt-img>` : ''}
          </button>
          <div class="ncm-fbt__info">
            <div class="ncm-fbt__topline">
              <a href="${window.Shopify?.routes?.root || '/'}products/${escapeHtml(product.handle)}" class="ncm-fbt__title">${escapeHtml(title)}</a>
              <span class="ncm-fbt__sale-badge" data-ncm-fbt-sale-badge hidden>Sale</span>
            </div>
            <div class="ncm-fbt__price" data-ncm-fbt-price></div>
            ${selects ? `<div class="ncm-fbt__options">${selects}</div>` : ''}
          </div>
        </div>`;
    }

    dropdownMarkup(product, name, optionIndex, itemIndex) {
      const id = `NcmFbt-${this.dataset.sectionId}-${itemIndex}-${optionIndex}`;
      return `
        <div class="ncm-fbt-dd" data-ncm-fbt-dropdown data-ncm-fbt-option="${optionIndex}">
          <button
            type="button"
            class="ncm-fbt-dd__button"
            id="${id}-button"
            aria-haspopup="listbox"
            aria-expanded="false"
            aria-controls="${id}-list"
            aria-label="${escapeHtml(`${product.title} – ${name}`)}"
          >
            <span class="ncm-fbt-dd__value" data-ncm-fbt-dd-value></span>
            <span class="ncm-fbt-dd__arrow" aria-hidden="true">
              <svg viewBox="0 0 20 20" fill="none"><path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </span>
          </button>
          <ul class="ncm-fbt-dd__list" id="${id}-list" role="listbox" tabindex="-1" hidden></ul>
        </div>`;
    }

    renderItem(item) {
      const { el, product } = item;
      if (!item.selected) item.selected = product.optionNames.map(() => null);
      this.applySelection(item);

      el.querySelectorAll('[data-ncm-fbt-dropdown]').forEach((dropdown) => this.renderDropdown(item, dropdown));

      const img = el.querySelector('[data-ncm-fbt-img]');
      const image = item.variant?.image || product.image;
      if (img && image) {
        img.src = sizedImage(image, 240);
        img.dataset.fullSrc = sizedImage(image, 1200);
      }

      const variant = item.variant;
      const onSale = Boolean(variant && variant.compare > variant.price);
      const priceEl = el.querySelector('[data-ncm-fbt-price]');
      if (priceEl) {
        priceEl.innerHTML = variant
          ? `<span>${escapeHtml(this.formatMoney(variant.price, true))}</span>${
              onSale ? ` <s>${escapeHtml(this.formatMoney(variant.compare, true))}</s>` : ''
            }`
          : '';
      }
      const badge = el.querySelector('[data-ncm-fbt-sale-badge]');
      if (badge) badge.hidden = !(onSale && this.config.showSaleBadges);
      if (variant?.id) el.dataset.variantId = variant.id;
      else delete el.dataset.variantId;
      el.classList.toggle('is-unavailable', !this.isPurchasable(item));
      el.setAttribute('aria-label', `${product.title}${variant ? `, ${variant.title}` : ''}`);
    }

    renderDropdown(item, dropdown) {
      const optionIndex = Number(dropdown.dataset.ncmFbtOption);
      const name = item.product.optionNames[optionIndex] || '';
      const values = item.product.optionValues[optionIndex] || [];
      const selected = item.selected[optionIndex];
      const placeholder = /size/i.test(name) ? 'Size' : /colou?r/i.test(name) ? 'Color' : name;
      const valueEl = dropdown.querySelector('[data-ncm-fbt-dd-value]');
      if (valueEl) valueEl.textContent = selected || placeholder;
      dropdown.classList.toggle('is-placeholder', !selected);
      dropdown.classList.toggle('is-unavailable', Boolean(selected) && !this.isValueAvailable(item, optionIndex, selected));

      const list = dropdown.querySelector('.ncm-fbt-dd__list');
      if (!list) return;
      list.innerHTML = values
        .map((value, index) => {
          const available = this.isValueAvailable(item, optionIndex, value);
          const isSelected = value === selected;
          return `<li
            role="option"
            class="ncm-fbt-dd__option${available ? '' : ' is-unavailable'}${isSelected ? ' is-selected' : ''}"
            id="${list.id}-opt-${index}"
            data-value="${escapeHtml(value)}"
            tabindex="-1"
            aria-selected="${isSelected}"
          ><span class="ncm-fbt-dd__option-label">${escapeHtml(value)}</span>${
            isSelected ? '<span class="ncm-fbt-dd__check" aria-hidden="true"></span>' : ''
          }</li>`;
        })
        .join('');
    }

    optionIndexByName(product, pattern) {
      return product.optionNames.findIndex((name) => pattern.test(name));
    }

    currentOptionNames() {
      const current = this.items?.find((item) => item.isCurrent);
      if (current) return current.product.optionNames;
      const rawOptions = this.currentProduct?.options || [];
      return rawOptions.map((option) => (typeof option === 'string' ? option : option.name)).filter(Boolean);
    }

    fieldsetValue(fieldset) {
      return fieldset?.querySelector('input:checked')?.value ?? fieldset?.querySelector('select')?.value ?? null;
    }

    // Size → Color is presentation only. Shopify option indexes stay catalog order (often Color, then Size).
    fieldsetCatalogIndex(fieldset, optionNames) {
      if (!fieldset || !optionNames?.length) return -1;
      if (fieldset.classList.contains('product-form__input--size')) {
        return optionNames.findIndex((name) => /size/i.test(name));
      }
      if (fieldset.classList.contains('ncm-color-swatches')) {
        return optionNames.findIndex((name) => /colou?r/i.test(name));
      }
      const named = fieldset.querySelector('input[name], select[name]');
      const position = named?.name?.match(/-(\d+)\s*$/);
      if (position) {
        const index = Number(position[1]) - 1;
        if (index >= 0 && index < optionNames.length) return index;
      }
      const label = (fieldset.querySelector('legend, .form__label, .ncm-size-header')?.textContent || '')
        .replace(/:.*/, '')
        .trim()
        .toLowerCase();
      if (!label) return -1;
      return optionNames.findIndex((name) => String(name).trim().toLowerCase() === label);
    }

    matchesSelection(variant, selected, ignoreIndex = -1) {
      return variant.options.every(
        (value, index) => index === ignoreIndex || selected[index] == null || value === selected[index]
      );
    }

    exactVariant(product, selected) {
      if (!selected || selected.some((value) => value == null)) return null;
      return product.variants.find((variant) => variant.options.every((value, index) => value === selected[index])) || null;
    }

    representativeVariant(product, selected) {
      return (
        product.variants.find((variant) => variant.available && this.matchesSelection(variant, selected)) ||
        product.variants.find((variant) => this.matchesSelection(variant, selected)) ||
        product.variants.find((variant) => variant.available) ||
        product.variants[0] ||
        null
      );
    }

    isValueAvailable(item, optionIndex, value) {
      return item.product.variants.some(
        (variant) =>
          variant.available && variant.options[optionIndex] === value && this.matchesSelection(variant, item.selected, optionIndex)
      );
    }

    isPurchasable(item) {
      const variant = this.exactVariant(item.product, item.selected);
      const sizeIndex = this.optionIndexByName(item.product, /size/i);
      if (sizeIndex >= 0 && item.selected[sizeIndex] == null) return false;
      return Boolean(variant?.available);
    }

    applySelection(item) {
      const exact = this.exactVariant(item.product, item.selected);
      if (exact) {
        item.variant = exact;
        return;
      }
      // Incomplete selections still need a preview variant for image/price.
      // A complete selection must never silently fall back to another variant.
      item.variant = item.selected.some((value) => value == null)
        ? this.representativeVariant(item.product, item.selected)
        : null;
    }

    initialSelected(product, isCurrent) {
      const selected = product.optionNames.map(() => null);
      const sizeIndex = this.optionIndexByName(product, /size/i);
      const colorIndex = this.optionIndexByName(product, /colou?r/i);
      const mainSize = this.getMainSize();

      if (isCurrent) {
        const mainSelection = this.getMainSelection();
        product.optionNames.forEach((_, index) => {
          selected[index] = mainSelection[index] ?? null;
        });
      } else if (sizeIndex >= 0 && mainSize && product.optionValues[sizeIndex]?.includes(mainSize)) {
        selected[sizeIndex] = mainSize;
      }

      if (colorIndex >= 0 && selected[colorIndex] == null) {
        const availableColor = product.optionValues[colorIndex]?.find((value) =>
          product.variants.some(
            (variant) =>
              variant.available &&
              variant.options[colorIndex] === value &&
              this.matchesSelection(variant, selected, colorIndex)
          )
        );
        selected[colorIndex] = availableColor || product.optionValues[colorIndex]?.[0] || null;
      }

      return selected;
    }

    onItemsClick(event) {
      const option = event.target.closest('[role="option"]');
      if (option) {
        event.preventDefault();
        this.chooseOption(option);
        return;
      }

      const toggle = event.target.closest('.ncm-fbt-dd__button');
      if (toggle) {
        event.preventDefault();
        this.toggleDropdown(toggle.closest('[data-ncm-fbt-dropdown]'));
        return;
      }

      const imageButton = event.target.closest('[data-ncm-fbt-image-button]');
      if (imageButton) this.openModal(imageButton);
    }

    onItemChange(event) {
      const itemEl = event.target.closest('.ncm-fbt__item');
      const item = this.items[Number(itemEl?.dataset.index)];
      if (!item) return;
      this.updateTotal();
    }

    chooseOption(option) {
      const dropdown = option.closest('[data-ncm-fbt-dropdown]');
      const itemEl = dropdown?.closest('.ncm-fbt__item');
      const item = this.items[Number(itemEl?.dataset.index)];
      if (!item) return;

      const changedIndex = Number(dropdown.dataset.ncmFbtOption);
      item.selected[changedIndex] = option.dataset.value;
      this.applySelection(item);

      if (item.isCurrent && /size/i.test(item.product.optionNames[changedIndex] || '')) {
        const size = item.selected[changedIndex];
        this.items
          .filter((other) => !other.isCurrent)
          .forEach((other) => {
            const index = this.optionIndexByName(other.product, /size/i);
            if (index < 0 || !other.product.optionValues[index]?.includes(size)) return;
            other.selected[index] = size;
            this.applySelection(other);
          });
      }

      this.items.forEach((entry) => this.renderItem(entry));
      this.closeDropdowns(true);
      this.updateTotal();
    }

    toggleDropdown(dropdown) {
      if (!dropdown) return;
      const wasOpen = dropdown.classList.contains('is-open');
      this.closeDropdowns();
      if (!wasOpen) this.openDropdown(dropdown);
    }

    openDropdown(dropdown) {
      const list = dropdown.querySelector('.ncm-fbt-dd__list');
      const button = dropdown.querySelector('.ncm-fbt-dd__button');
      if (!list || !button) return;
      dropdown.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      list.hidden = false;
      this.openMenu = dropdown;
      this.positionOpenDropdown();
      const selected = list.querySelector('[aria-selected="true"]') || list.querySelector('[role="option"]');
      if (selected) {
        const listRect = list.getBoundingClientRect();
        const optionRect = selected.getBoundingClientRect();
        if (optionRect.bottom > listRect.bottom) list.scrollTop += optionRect.bottom - listRect.bottom;
        if (optionRect.top < listRect.top) list.scrollTop -= listRect.top - optionRect.top;
        selected.focus({ preventScroll: true });
      }
    }

    closeDropdowns(restoreFocus = false) {
      const open = this.openMenu || this.querySelector('.ncm-fbt-dd.is-open');
      this.querySelectorAll('.ncm-fbt-dd.is-open').forEach((dropdown) => {
        dropdown.classList.remove('is-open');
        dropdown.querySelector('.ncm-fbt-dd__button')?.setAttribute('aria-expanded', 'false');
        const list = dropdown.querySelector('.ncm-fbt-dd__list');
        if (list) {
          list.hidden = true;
          list.removeAttribute('style');
        }
      });
      if (restoreFocus && open) open.querySelector('.ncm-fbt-dd__button')?.focus({ preventScroll: true });
      this.openMenu = null;
    }

    positionOpenDropdown() {
      const dropdown = this.openMenu;
      if (!dropdown) return;
      const button = dropdown.querySelector('.ncm-fbt-dd__button');
      const list = dropdown.querySelector('.ncm-fbt-dd__list');
      if (!button || !list || list.hidden) return;

      const rect = button.getBoundingClientRect();
      const gutter = 8;
      const maxHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom - gutter;
      const spaceAbove = rect.top - gutter;
      const needed = Math.min(maxHeight, list.scrollHeight || maxHeight);
      const openUp = spaceBelow < needed && spaceAbove > spaceBelow;
      const height = Math.min(maxHeight, Math.max(88, openUp ? spaceAbove : spaceBelow));
      const width = rect.width;
      const left = Math.min(Math.max(gutter, rect.left), window.innerWidth - width - gutter);

      list.style.position = 'fixed';
      list.style.left = `${Math.round(left)}px`;
      list.style.width = `${Math.round(width)}px`;
      list.style.maxHeight = `${Math.round(height)}px`;
      list.style.zIndex = '1300';
      if (openUp) {
        list.style.top = 'auto';
        list.style.bottom = `${Math.round(window.innerHeight - rect.top + 4)}px`;
      } else {
        list.style.bottom = 'auto';
        list.style.top = `${Math.round(rect.bottom + 4)}px`;
      }
    }

    onDocumentPointer(event) {
      if (this.openMenu && !event.target.closest?.('[data-ncm-fbt-dropdown]')) this.closeDropdowns();
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.els.modal && !this.els.modal.hidden) {
        this.closeModal();
        return;
      }
      if (this.openMenu) {
        event.preventDefault();
        this.closeDropdowns(true);
      }
    }

    onDropdownKeydown(event) {
      const dropdown = event.target.closest('[data-ncm-fbt-dropdown]');
      if (!dropdown) return;
      const options = [...dropdown.querySelectorAll('[role="option"]')];
      if (!options.length) return;

      if (event.target.matches('.ncm-fbt-dd__button') && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        if (!dropdown.classList.contains('is-open')) this.openDropdown(dropdown);
        return;
      }

      if (!dropdown.classList.contains('is-open')) return;

      const current = document.activeElement?.closest('[role="option"]');
      const index = Math.max(0, options.indexOf(current));
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        options[Math.min(options.length - 1, index + 1)]?.focus({ preventScroll: true });
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        options[Math.max(0, index - 1)]?.focus({ preventScroll: true });
      } else if (event.key === 'Home') {
        event.preventDefault();
        options[0]?.focus({ preventScroll: true });
      } else if (event.key === 'End') {
        event.preventDefault();
        options[options.length - 1]?.focus({ preventScroll: true });
      } else if (event.key === 'Enter' || event.key === ' ') {
        if (current) {
          event.preventDefault();
          this.chooseOption(current);
        }
      }
    }

    getMainSelection() {
      const optionNames = this.currentOptionNames();
      const selected = optionNames.map(() => null);
      const live = this.mainProduct?.selectionState?.selection;
      if (Array.isArray(live) && live.length) {
        optionNames.forEach((_, index) => {
          selected[index] = live[index] ?? null;
        });
        return selected;
      }

      const fieldsets = this.mainProduct?.querySelectorAll('variant-selects .product-form__input') || [];
      [...fieldsets].forEach((fieldset) => {
        const index = this.fieldsetCatalogIndex(fieldset, optionNames);
        if (index < 0 || index >= selected.length) return;
        selected[index] = this.fieldsetValue(fieldset);
      });
      return selected;
    }

    getMainSize() {
      const names = this.currentOptionNames();
      const sizeIndex = names.findIndex((name) => /size/i.test(name));
      return sizeIndex >= 0 ? this.getMainSelection()[sizeIndex] || null : null;
    }

    syncCurrentItemFromMain() {
      const current = this.items.find((item) => item.isCurrent);
      if (!current) return;
      const selection = this.getMainSelection();
      current.selected = current.product.optionNames.map((_, index) => selection[index] ?? null);
      this.applySelection(current);
    }

    onMainOptionChange(event) {
      const fieldset = event.target.closest('variant-selects .product-form__input');
      if (!fieldset || !this.mainProduct?.contains(fieldset) || !this.items.length) return;

      this.syncCurrentItemFromMain();

      const current = this.items.find((item) => item.isCurrent);
      const names = current?.product.optionNames || this.currentOptionNames();
      const sizeIndex = names.findIndex((name) => /size/i.test(name));
      const changedIndex = this.fieldsetCatalogIndex(fieldset, names);
      const isSizeChange = changedIndex === sizeIndex || fieldset.matches('.product-form__input--size');

      if (isSizeChange) {
        const size = this.getMainSize();
        if (size) {
          this.items
            .filter((item) => !item.isCurrent)
            .forEach((item) => {
              const index = this.optionIndexByName(item.product, /size/i);
              if (index < 0 || !item.product.optionValues[index]?.includes(size)) return;
              item.selected[index] = size;
              this.applySelection(item);
            });
        }
      }

      this.closeDropdowns();
      this.items.forEach((item) => this.renderItem(item));
      this.updateTotal();
    }

    get selectedItems() {
      return this.items.filter((item) => item.el?.querySelector('[data-ncm-fbt-check]')?.checked);
    }

    /*
      Estimates only: Shopify calculates the real discount in the cart. Sale savings compare against
      compare-at prices; the bundle discount is taken from eligible selling prices.
    */
    calculate() {
      const selected = this.selectedItems;
      const totals = { saleTotal: 0, compareTotal: 0, eligibleSubtotal: 0, eligibleQuantity: 0 };
      selected.forEach(({ variant, product, quantity }) => {
        if (!variant) return;
        totals.saleTotal += variant.price * quantity;
        totals.compareTotal += Math.max(variant.compare, variant.price) * quantity;
        if (this.isEligibleType(product.type)) {
          totals.eligibleSubtotal += variant.price * quantity;
          totals.eligibleQuantity += quantity;
        }
      });

      const quantity = totals.eligibleQuantity + this.cart.eligibleQuantity;
      const tier = selected.length && totals.eligibleQuantity ? this.tierFor(quantity) : null;
      const bundleSavings = tier ? Math.round((totals.eligibleSubtotal * tier.percent) / 100) : 0;
      const finalTotal = totals.saleTotal - bundleSavings;

      return {
        ...totals,
        selectedCount: selected.length,
        quantity,
        tier,
        next: this.nextTier(quantity, tier),
        saleSavings: totals.compareTotal - totals.saleTotal,
        bundleSavings,
        finalTotal,
      };
    }

    updateTotal() {
      const result = this.calculate();
      const { messages } = this.config;
      const { els } = this;
      const tiers = this.activeTiers;
      const totalSavings = result.saleSavings + result.bundleSavings;

      if (els.saveBadge && els.saveText) {
        els.saveBadge.hidden = totalSavings <= 0;
        els.saveText.textContent = `You save ${this.formatMoney(totalSavings)}`;
      }

      if (els.saleRow) els.saleRow.hidden = result.saleSavings <= 0;
      if (els.saleSavings) els.saleSavings.textContent = `−${this.formatMoney(result.saleSavings)}`;
      if (els.bundleRow) els.bundleRow.hidden = result.bundleSavings <= 0;
      if (els.bundleLabel && result.tier) {
        els.bundleLabel.textContent = fillTemplate(messages.bundleLabel || 'Bundle discount ({percent}%, est.)', {
          percent: result.tier.percent,
        });
      }
      if (els.bundleSavings) els.bundleSavings.textContent = `−${this.formatMoney(result.bundleSavings)}`;
      if (els.breakdown) els.breakdown.hidden = result.saleSavings <= 0 && result.bundleSavings <= 0;
      if (els.note) els.note.hidden = !tiers.length;

      this.updateBundleStatus(result, tiers);
      this.updateShipping(result);

      if (els.statusRow) els.statusRow.hidden = (els.bundleStatus?.hidden ?? true) && (els.shippingNote?.hidden ?? true);
      if (els.wasPrice) els.wasPrice.hidden = result.compareTotal <= result.finalTotal;
      if (els.originalTotal) els.originalTotal.textContent = this.formatMoney(result.compareTotal);
      if (els.total) els.total.textContent = this.formatMoney(result.finalTotal);
      if (els.button && els.button.getAttribute('aria-busy') !== 'true') {
        els.button.disabled = result.selectedCount === 0;
        els.button.textContent = this.config.buttonLabel;
      }
    }

    updateBundleStatus({ tier, next, quantity, selectedCount }, tiers) {
      const { bundleStatus, bundleText, bundleIcon } = this.els;
      if (!bundleStatus || !bundleText || !bundleIcon) return;
      const { messages } = this.config;

      bundleStatus.hidden = !tiers.length || !selectedCount;
      bundleStatus.classList.remove('is-active', 'is-pending');
      if (bundleStatus.hidden) {
        this.lastTierKey = null;
        return;
      }

      const nextText = () => {
        const count = next.quantity - quantity;
        return fillTemplate(messages.next || 'Add {count} more {item} to unlock {percent}% off', {
          count,
          item: this.itemWord(count),
          percent: next.percent,
        });
      };

      let text = '';
      if (tier) {
        bundleStatus.classList.add('is-active');
        bundleIcon.innerHTML = ICONS.check;
        if (next) {
          const count = next.quantity - quantity;
          text = fillTemplate(messages.unlockedNext || '{percent}% unlocked! Add {count} more {item} to get {next_percent}% off', {
            percent: tier.percent,
            count,
            item: this.itemWord(count),
            next_percent: next.percent,
          });
        } else {
          text = fillTemplate(messages.unlocked || '{percent}% bundle discount unlocked!', { percent: tier.percent });
        }
        if (this.lastTierKey !== tier.key) this.celebrate(bundleStatus);
      } else if (next) {
        bundleStatus.classList.add('is-pending');
        bundleIcon.innerHTML = ICONS.shirt;
        text = nextText();
      } else {
        bundleStatus.hidden = true;
      }

      const inCart = this.cart.eligibleQuantity;
      if (text && inCart > 0 && messages.inCart) {
        text += ` · ${fillTemplate(messages.inCart, { count: inCart, item: this.itemWord(inCart) })}`;
      }
      bundleText.textContent = text;
      this.lastTierKey = tier?.key ?? null;
    }

    // Free shipping is judged on what Shopify would see: the current cart total plus this estimated bundle.
    updateShipping({ finalTotal, selectedCount }) {
      const { shippingNote, shippingText, shippingIcon } = this.els;
      if (!shippingNote || !shippingText) return;
      const { freeShipping } = this.config;
      const threshold = Math.round((Number(freeShipping.threshold) || 0) * (Number(window.Shopify?.currency?.rate) || 1));

      shippingNote.hidden = threshold <= 0 || !selectedCount;
      shippingNote.classList.remove('is-unlocked', 'is-pending');
      if (shippingNote.hidden) return;

      const subtotal = this.cart.total + finalTotal;
      const unlocked = subtotal >= threshold;
      if (unlocked) {
        shippingIcon.innerHTML = ICONS.truck(1.8);
        shippingNote.classList.add('is-unlocked');
        shippingText.textContent = freeShipping.unlocked || 'Free shipping unlocked';
        if (!this.wasShippingUnlocked) this.celebrate(shippingNote);
      } else {
        shippingIcon.innerHTML = ICONS.truck(1.5);
        shippingNote.classList.add('is-pending');
        shippingText.textContent = fillTemplate(freeShipping.remaining || 'Add {amount} more for free shipping', {
          amount: this.formatMoney(threshold - subtotal),
        });
      }
      this.wasShippingUnlocked = unlocked;
    }

    celebrate(el) {
      if (this.reducedMotion) return;
      el.classList.add('is-celebrating');
      this.els.statusRow?.classList.add('is-celebrating');
      const cleanup = () => {
        el.classList.remove('is-celebrating');
        this.els.statusRow?.classList.remove('is-celebrating');
      };
      el.addEventListener('animationend', cleanup, { once: true, signal: this.controller.signal });
      window.setTimeout(cleanup, 950);
    }

    async addToCart() {
      const { button, message } = this.els;
      if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

      const ready = this.selectedItems.filter((item) => this.isPurchasable(item));
      if (this.selectedItems.some((item) => !this.isPurchasable(item))) {
        message.textContent = 'Choose an available size and color for each selected shirt.';
        return;
      }

      const items = ready.map((item) => {
        const variant = this.exactVariant(item.product, item.selected);
        return { id: Number(variant.id), quantity: item.quantity };
      });

      if (!items.length) {
        message.textContent = 'Select at least one item.';
        return;
      }

      message.textContent = '';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding…';

      const drawer = document.querySelector('cart-drawer');
      const sections = drawer ? drawer.getSectionsToRender().map((section) => section.id) : [];
      const pricingSeq = drawer?.nextSeq?.();
      drawer?.beginPricing?.({ announce: 'Updating cart totals' });
      const resetButton = () => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = this.config.buttonLabel;
      };

      try {
        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items,
            ...(sections.length ? { sections, sections_url: window.location.pathname } : {}),
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) throw new Error(data.description || data.message || '');

        const synced = await this.bundle?.syncCode({ sections }).catch(() => null);

        // Without the drawer (cart type Page/Notification) the cart page shows the result.
        if (!drawer) {
          window.location.href = synced?.redirect || this.cartUrl;
          return;
        }

        const state = synced?.cart?.sections ? synced.cart : data;
        drawer.setActiveElement(button);
        drawer.renderContents(state, {
          open: true,
          added: data.items || [],
          fromSync: true,
          seq: pricingSeq,
        });
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ncm-fbt', cartData: state });
        resetButton();
      } catch (error) {
        message.textContent = error.message || 'Could not add items. Try again.';
        resetButton();
      } finally {
        drawer?.endPricing?.();
      }
    }

    // The NCM Bundle Teaser block renders its own copy; this reveals it and points it at this section.
    showTeaser() {
      this.teaserController?.abort();
      const teaser = this.mainProduct?.querySelector('[data-ncm-fbt-teaser]');
      const section = this.closest('section');
      if (!teaser || !section?.id || !this.items.length) return;
      this.teaser = teaser;
      teaser.dataset.ncmFbtSource = this.dataset.sectionId;
      teaser.hidden = false;
      if (!teaser.hasAttribute('data-ncm-fbt-teaser-link')) return;

      teaser.href = `#${section.id}`;
      this.teaserController = new AbortController();
      teaser.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          section.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
          const heading = section.querySelector('h2');
          heading?.setAttribute('tabindex', '-1');
          heading?.focus({ preventScroll: true });
        },
        { signal: this.teaserController.signal }
      );
    }

    openModal(imageButton) {
      const img = imageButton.querySelector('img');
      if (!img || !this.els.modal) return;
      this.modalOpener = imageButton;
      this.els.modalImg.src = img.dataset.fullSrc || img.src;
      this.els.modalImg.alt = img.alt;
      this.els.modal.hidden = false;
      this.toggleScrollLock(true);
      this.els.modalClose?.focus();
    }

    closeModal() {
      this.els.modal.hidden = true;
      this.els.modalImg.removeAttribute('src');
      this.toggleScrollLock(false);
      this.modalOpener?.focus();
    }

    toggleScrollLock(locked) {
      document.documentElement.classList.toggle('ncm-fbt-modal-open', locked);
      document.body.classList.toggle('ncm-fbt-modal-open', locked);
    }
  }

  customElements.define('ncm-auto-fbt', NcmAutoFbt);
}
